import { CAST_AI } from '@cast/constants';
import { ViewComponentsResponse } from '@cast/types-cluster-management';

export const generateClusterManagementComponents =
  (): ViewComponentsResponse => {
    return {
      components: [
        {
          id: 'component-id-kvisor',
          name: 'kvisor',
          descriptionTitle: 'Image scanning',
          description:
            "Scans container images for vulnerabilities and provides security insights to improve your cluster's security posture through continuous monitoring.",
          pricingType: 'PAID',
          latestVersion: '1.0.75',
          clusterCount: 1,
          statuses: [],
          featureId: '56dd617c-7032-4a1f-90c1-869efe03c32c',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-workload-autoscaler',
          name: 'workload-autoscaler',
          descriptionTitle: 'Workload autoscaling',
          description:
            'Dynamically adjusts container resource requests based on historical usage patterns to optimize workload efficiency and prevent resource waste.',
          pricingType: 'PAID',
          latestVersion: '0.1.86',
          clusterCount: 1,
          statuses: [],
          featureId: '24ca2e41-05de-4cb1-ac5a-429d847c6cf3',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-castai-gpu-metrics-exporter',
          name: 'castai-gpu-metrics-exporter',
          descriptionTitle: 'GPU monitoring',
          description:
            'Collects GPU utilization metrics from cluster nodes to enable optimization of GPU resources and provide visibility into GPU workload costs.',
          pricingType: 'PAID',
          latestVersion: '0.1.19',
          clusterCount: 1,
          statuses: [],
          featureId: '13de15b7-5a8c-438b-9703-05c2b348fb97',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-audit-logs-receiver',
          name: 'audit-logs-receiver',
          descriptionTitle: 'Audit logs event exporting',
          description:
            'Captures and exports detailed audit logs of all operations and changes in your cluster for security compliance and operational transparency.',
          pricingType: 'PAID',
          latestVersion: '0.5.1',
          clusterCount: 1,
          statuses: [],
          featureId: '5b64317e-4402-45fc-873c-6c04f60639f9',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-pod-pinner',
          name: 'pod-pinner',
          descriptionTitle: 'Cost optimization',
          description:
            'Coordinates pod scheduling with the autoscaler to ensure optimal pod placement on specific nodes, enhancing resource efficiency and cost savings.',
          pricingType: 'PAID',
          latestVersion: '1.2.0',
          clusterCount: 1,
          statuses: [],
          featureId: '49909b64-67ee-4929-aa03-63308458c85f',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-castai-agent',
          name: 'castai-agent',
          descriptionTitle: 'Cost reporting and optimization',
          description: `Synchronizes cluster state with ${CAST_AI}, providing comprehensive monitoring and data collection for cost reporting and optimization recommendations.`,
          pricingType: 'FREE',
          latestVersion: '0.97.0',
          clusterCount: 1,
          statuses: [],
          featureId: '49909b64-67ee-4929-aa03-63308458c85f',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-spot-handler',
          name: 'spot-handler',
          descriptionTitle: 'Spot interruption handling',
          description:
            'Monitors Spot Instances for interruption events and coordinates graceful workload transitions to minimize disruption during Spot Instance terminations.',
          pricingType: 'PAID',
          latestVersion: '0.26.0',
          clusterCount: 0,
          statuses: [
            {
              status: 'FAILED',
              count: 1,
            },
            {
              status: 'WARNING',
              count: 1,
            },
          ],
          featureId: '49909b64-67ee-4929-aa03-63308458c85f',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-db-optimizer',
          name: 'db-optimizer',
          descriptionTitle: 'DB optimizer product',
          description:
            'Implements database query caching and optimization to reduce database load and costs while improving application performance.',
          pricingType: 'PAID',
          latestVersion: '0.17.3',
          clusterCount: 1,
          statuses: [],
          featureId: '0d18a5f7-5140-43ac-8240-0223317edf60',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-pod-mutator',
          name: 'pod-mutator',
          descriptionTitle: 'Cost optimization',
          description:
            'Automatically transforms pod specifications at creation time using configurable templates, enabling consistent resource allocation, bulk label modifications, and seamless workload migrations without changing deployment manifests.',
          pricingType: 'PAID',
          latestVersion: '0.0.16',
          clusterCount: 0,
          statuses: [],
          featureId: '49909b64-67ee-4929-aa03-63308458c85f',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-egressd-exporter',
          name: 'egressd-exporter',
          descriptionTitle: 'Network metrics',
          description:
            'Monitors network traffic between workloads to provide visibility into data transfer costs and help optimize network communication patterns.',
          pricingType: 'PAID',
          latestVersion: '0.1.42',
          clusterCount: 1,
          statuses: [],
          featureId: '56dd617c-7032-4a1f-90c1-869efe03c32c',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-ai-optimizer-proxy',
          name: 'ai-optimizer-proxy',
          descriptionTitle: 'Self-hosted LLM deployment',
          description:
            'Routes LLM requests to the most cost-effective AI provider based on complexity and requirements, optimizing AI inference costs.',
          pricingType: 'PAID',
          latestVersion: '0.0.17',
          clusterCount: 1,
          statuses: [],
          featureId: 'f892b414-a8be-4623-b35b-05ebf345e090',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-cluster-controller',
          name: 'cluster-controller',
          descriptionTitle: 'Kubernetes management',
          description:
            'Manages Kubernetes operations like node draining, labeling, and CSR approval to ensure smooth cluster administration and node lifecycle management.',
          pricingType: 'PAID',
          latestVersion: '0.80.2',
          clusterCount: 1,
          statuses: [],
          featureId: '49909b64-67ee-4929-aa03-63308458c85f',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'component-id-evictor',
          name: 'evictor',
          descriptionTitle: 'Node deletion policy',
          description:
            'Implements intelligent bin-packing algorithms to consolidate workloads onto fewer nodes, creating empty nodes that can be removed to reduce costs.',
          pricingType: 'PAID',
          latestVersion: '0.31.51',
          clusterCount: 1,
          statuses: [],
          featureId: '49909b64-67ee-4929-aa03-63308458c85f',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
        {
          id: 'castware-operator',
          name: 'castware-operator',
          descriptionTitle: 'Component lifecycle management',
          description:
            'Automates the installation, updates, and version management of components in your cluster.',
          pricingType: 'FREE',
          latestVersion: '',
          clusterCount: 1,
          statuses: [],
          featureId: 'ff75cb12-a31f-47c5-83bb-d5e6d3ab6705',
          supportedProviders: ['AKS', 'EKS', 'GKE'],
        },
      ],
      clusterCount: 1,
    };
  };
